/**
 * Test page for VOY-101: Multi-Airport Region Search
 * Demonstrates the new region airport search feature
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiAirportSelector } from '@/components/search/multi-airport-selector';
import { useRegionAirports } from '@/hooks/useRegionAirports';
import { Search, MapPin, Plane, Sparkles } from 'lucide-react';

export default function TestRegionAirportsPage() {
  const [regionQuery, setRegionQuery] = useState('');
  const [airports, setAirports] = useState<any[]>([]);
  const [selectedAirport, setSelectedAirport] = useState('');
  const [showSelector, setShowSelector] = useState(false);
  
  const { getRegionAirports, shouldSearchRegion, loading } = useRegionAirports();

  const handleSearch = async () => {
    if (!regionQuery.trim()) return;

    // Reset selection when searching new region
    setSelectedAirport('');

    // Check if it's a region
    const isRegion = shouldSearchRegion(regionQuery);
    
    if (isRegion) {
      const results = await getRegionAirports(regionQuery);
      setAirports(results);
      setShowSelector(results.length > 0);
    } else {
      alert(`"${regionQuery}" is not detected as a region. Try: California, Florida, Texas, Bay Area`);
    }
  };

  const handleSelectAirport = (code: string) => {
    setSelectedAirport(code);
    setShowSelector(false);
    alert(`Selected: ${code}\n\nIn the real search flow, this would:\n1. Set destination to ${code}\n2. Continue with flight/hotel search`);
  };

  const handleSelectAll = () => {
    setShowSelector(false);
    alert(`Search All ${airports.length} airports!\n\nIn the real flow, this would:\n1. Search flights from ALL airports\n2. Compare prices\n3. Show best deals`);
  };

  const handleCancel = () => {
    setShowSelector(false);
  };

  return (
    <div className="container max-w-5xl mx-auto p-8">
      {/* Hero Card */}
      <Card className="mb-8 bg-gradient-to-r from-[#5B6FED] to-[#8B9FED] text-white border-0">
        <CardContent className="p-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-2">VOY-101: Multi-Airport Region Search</h1>
              <p className="text-white/90 text-lg">
                Test the new dynamic region-to-airports feature
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Search Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#5B6FED]" />
            Search Airports by Region
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Search Input */}
          <div className="flex gap-2">
            <Input
              value={regionQuery}
              onChange={(e) => setRegionQuery(e.target.value)}
              placeholder="Enter region name (e.g., California, Florida, Bay Area)"
              className="flex-1 text-base"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              onClick={handleSearch}
              disabled={loading || !regionQuery.trim()}
              className="bg-[#5B6FED] hover:bg-[#4A5FDD] px-6"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>

          {/* Quick Test Buttons */}
          <div>
            <p className="text-sm text-muted-foreground mb-2">Quick tests:</p>
            <div className="flex flex-wrap gap-2">
              {[
                'California',
                'Florida', 
                'Texas',
                'Bay Area',
                'New York',
                'Nevada',
                'Arizona',
                'Catalonia',
                'Tuscany'
              ].map((region) => (
                <Button
                  key={region}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRegionQuery(region);
                    // Reset selection when searching new region
                    setSelectedAirport('');
                    setTimeout(async () => {
                      const results = await getRegionAirports(region);
                      setAirports(results);
                      setShowSelector(results.length > 0);
                    }, 100);
                  }}
                  className="rounded-full"
                >
                  {region}
                </Button>
              ))}
            </div>
          </div>

          {/* Multi-Airport Selector */}
          {showSelector && (
            <MultiAirportSelector
              region={regionQuery}
              airports={airports}
              onSelect={handleSelectAirport}
              onSelectAll={handleSelectAll}
              onCancel={handleCancel}
              selectedCode={selectedAirport}
            />
          )}

          {/* Results Summary (when not showing selector) */}
          {!showSelector && airports.length > 0 && (
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-green-900">
                  <Plane className="w-5 h-5" />
                  <span className="font-medium">
                    ✅ Found {airports.length} airports in {regionQuery}
                  </span>
                </div>
                {selectedAirport && (
                  <p className="text-sm text-green-700 mt-2">
                    Selected: {selectedAirport} - {airports.find(a => a.code === selectedAirport)?.city}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* How It Works Card */}
      <Card className="mt-6 border-[#5B6FED]">
        <CardHeader>
          <CardTitle className="text-lg">How It Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-[#E8E8FF] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-[#5B6FED] text-xs font-bold">1</span>
            </div>
            <div>
              <strong>Region Detection:</strong> System checks if your query is a region (state, province, metro area)
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-[#E8E8FF] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-[#5B6FED] text-xs font-bold">2</span>
            </div>
            <div>
              <strong>Geocoding:</strong> Gets the geographic bounding box for the region (coordinates)
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-[#E8E8FF] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-[#5B6FED] text-xs font-bold">3</span>
            </div>
            <div>
              <strong>Airport Query:</strong> Finds all airports within that geographic area
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-[#E8E8FF] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-[#5B6FED] text-xs font-bold">4</span>
            </div>
            <div>
              <strong>Smart Sorting:</strong> Sorts airports by distance from region center
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-[#E8E8FF] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-[#5B6FED] text-xs font-bold">5</span>
            </div>
            <div>
              <strong>User Choice:</strong> Let user pick one airport or search all for best prices
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tech Details Card */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-lg text-blue-900">Technical Implementation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-blue-800">
          <div>
            <strong>Backend API:</strong> GET /api/location/airports/region
          </div>
          <div>
            <strong>Database:</strong> 958 airports with coordinates (55% coverage)
          </div>
          <div>
            <strong>Geocoding:</strong> Nominatim OpenStreetMap API
          </div>
          <div>
            <strong>Algorithm:</strong> Bounding box search + distance sorting
          </div>
          <div>
            <strong>Performance:</strong> ~1-2s including geocoding API call
          </div>
          <div>
            <strong>Fully Dynamic:</strong> No static tables, works for ANY region worldwide
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

